import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { Field, Input } from '../components/ui/Field';
import { Icon } from '../components/ui/Icon';
import { LoadingState } from '../components/ui/AsyncBoundary';

/**
 * Sign in.
 *
 * The error message is whatever the server said, which is deliberately the
 * same for a wrong password and an unknown account — telling those apart would
 * let anyone enumerate who has an account here. The lockout message is
 * different, because a locked-out user needs to know to come back later rather
 * than keep trying.
 */
export function Login() {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);

  if (status === 'loading') return <LoadingState className="h-screen" label="Restoring your session" />;
  if (status === 'authenticated') return <Navigate to={location.state?.from?.pathname || '/'} replace />;

  const onSubmit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await signIn(email.trim(), password);
      navigate(location.state?.from?.pathname || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Sign-in failed.');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1fr_460px]">
      <Hero />

      <div className="flex items-center justify-center bg-surface px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-7 lg:hidden">
            <BrandMark />
          </div>

          <h1 className="text-[24px] font-semibold tracking-tight text-ink">Sign in</h1>
          <p className="mt-1 text-[13.5px] text-ink-3">
            Use the account your fleet administrator set up for you.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <Field label="Email">
              <Input
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                invalid={Boolean(error)}
              />
            </Field>

            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                invalid={Boolean(error)}
              />
            </Field>

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-critical/30 bg-critical-soft px-3 py-2 text-[13px] text-critical-ink"
              >
                <Icon name="critical" size="sm" className="mt-0.5" />
                {error}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full justify-center"
              loading={pending}
              disabled={!email || !password}
            >
              Sign in
            </Button>
          </form>

          <p className="mt-6 text-center text-[12px] text-ink-3">
            Trouble signing in? Your organisation administrator can reset your access.
          </p>
        </div>
      </div>
    </div>
  );
}

function BrandMark({ large = false }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`relative flex items-center justify-center rounded-xl bg-brand ${
          large ? 'h-11 w-11' : 'h-9 w-9'
        }`}
      >
        <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-surface bg-accent" />
        <Icon name="truck" size={large ? 'lg' : 'md'} className="text-white" strokeWidth={2} />
      </span>
      <div>
        <p className={`font-semibold tracking-tight text-ink ${large ? 'text-[19px]' : 'text-[16px]'}`}>
          Fleet Telematics
        </p>
        <p className="text-[12px] text-ink-3">Track, analyse, and keep the fleet healthy</p>
      </div>
    </div>
  );
}

/**
 * The left panel. Blue ground, orange accent, and a schematic of the thing the
 * product actually does — a route with vehicles on it — rather than a stock
 * photograph.
 */
function Hero() {
  return (
    <div className="relative hidden overflow-hidden bg-brand lg:flex lg:flex-col lg:justify-between lg:p-10">
      <div className="relative z-10">
        <div className="flex items-center gap-3">
          <span className="relative flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 backdrop-blur">
            <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-brand bg-accent" />
            <Icon name="truck" size="lg" className="text-white" strokeWidth={2} />
          </span>
          <p className="text-[17px] font-semibold text-white">Fleet Telematics</p>
        </div>
      </div>

      <div className="relative z-10 max-w-md">
        <h2 className="text-[34px] font-semibold leading-[1.15] tracking-tight text-white">
          Every vehicle, every packet, in one place.
        </h2>
        <p className="mt-3 text-[15px] leading-relaxed text-white/75">
          Live position and telemetry from your TCU fleet, with history, trip analysis and device
          health — scoped to exactly what each person should see.
        </p>

        <ul className="mt-7 space-y-2.5">
          {[
            'Live tracking with sub-minute updates',
            'Route playback and trip history',
            'Device health scored on seven signals',
            'Role-based access down to a single vehicle',
          ].map((line) => (
            <li key={line} className="flex items-center gap-2.5 text-[13.5px] text-white/85">
              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent">
                <Icon name="check" size="xs" className="text-white" strokeWidth={2.5} />
              </span>
              {line}
            </li>
          ))}
        </ul>
      </div>

      <RouteMotif />
    </div>
  );
}

/** A route line with vehicle dots — the product's own subject, drawn simply. */
function RouteMotif() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.18]"
      viewBox="0 0 460 900"
      fill="none"
      aria-hidden="true"
      preserveAspectRatio="xMidYMid slice"
    >
      <path
        d="M-40 720 C 90 690, 120 560, 220 540 S 380 470, 400 340 S 330 150, 430 60"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="1 14"
      />
      <path
        d="M-60 830 C 120 800, 200 700, 300 660 S 460 560, 500 430"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {[
        [220, 540],
        [400, 340],
        [300, 660],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="7" fill="white" />
      ))}
    </svg>
  );
}
